import React, { useState } from 'react'
import { useTheme } from '@mui/material/styles'
import {
  Box,
  CircularProgress,
  FormLabel,
  Grid,
  Modal,
  styled,
  Typography,
  Chip,
  Divider,
  Fade,
  IconButton,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
} from '@mui/material'
import lightTheme from '../../styles/theme/lightTheme'
import '../../styles/components/_box.scss'
import '../../styles/components/_grids.scss'
import '../../styles/components/_inputs.scss'
import moment from 'moment-timezone'
import {
  faClose,
  faFile,
  faCalendar,
  faBuilding,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { toast, ToastContainer, Slide } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import '../../styles/components/_toasts.scss'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import { ROLE_ENTREPRISE } from '../../data'
import { downloadFile, FILE_URL } from '../../config'
import Api from '../../services/api.service'
import { handlingErrors } from '../../utils'
import OutlineButton from '../buttons/OutlineButton'
import GradientButton from '../buttons/GradientButton'

const ModalContainer = styled(Box)(({ theme }) => ({
  width: 'calc(100% - 32px)',
  maxWidth: 800,
  minHeight: 400,
  maxHeight: '90vh',
  backgroundColor: '#FFFFFF',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  borderRadius: '16px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',

  '&:focus-visible': {
    outline: 'none',
  },

  [theme.breakpoints.up('sm')]: {
    width: 800,
    padding: '20px 40px',
  },
}))

const BoxContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  maxHeight: 600,
  overflow: 'auto',
}))

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontSize: '0.875rem',
  fontWeight: 'bold',
  padding: '4px 12px',
  ...(status === 'postule' && {
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
    color: '#ff9800',
  }),
  ...(status === 'accepte' && {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    color: '#4caf50',
  }),
  ...(status === 'refuse' && {
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
    color: '#f44336',
  }),
  ...(status === 'entretien' && {
    backgroundColor: 'rgba(33, 150, 243, 0.1)',
    color: '#2196f3',
  }),
}))

const InfoSection = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
  padding: '16px',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  borderRadius: '12px',
  border: '1px solid rgba(102, 126, 234, 0.15)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.3s ease',
  '@media (hover: hover)': {
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-2px)',
    },
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: '20px',
    padding: '20px',
  },
}))

const HeaderSection = styled(Box)(({ theme }) => ({
  background:
    'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
  borderRadius: '12px',
  padding: '16px',
  border: '1px solid rgba(102, 126, 234, 0.2)',
  marginBottom: '16px',
  [theme.breakpoints.up('sm')]: {
    padding: '24px',
    marginBottom: '20px',
  },
}))

const InterviewSection = styled(Box)(({ theme }) => ({
  background:
    'linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(21, 101, 192, 0.05) 100%)',
  borderRadius: '12px',
  padding: '16px',
  border: '1px solid rgba(33, 150, 243, 0.2)',
  marginBottom: '16px',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
    marginBottom: '20px',
  },
}))

const NotesSection = styled(Box)(({ theme }) => ({
  background:
    'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(56, 142, 60, 0.05) 100%)',
  borderRadius: '12px',
  padding: '16px',
  border: '1px solid rgba(76, 175, 80, 0.2)',
  marginBottom: '16px',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
    marginBottom: '20px',
  },
}))

const DetailCandidature = ({
  open,
  onClose,
  onSave,
  candidature,
  role,
  toast,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [loading, setLoading] = useState(false)
  const [editedStatus, setEditedStatus] = useState(candidature?.status || '')
  const [companyNotes, setCompanyNotes] = useState(
    candidature?.notesEntreprise || ''
  )
  const [cvFile, setCvFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [interviewDate, setInterviewDate] = useState(
    candidature?.dateEntretien
      ? moment(candidature.dateEntretien).format('YYYY-MM-DDTHH:mm')
      : ''
  )
  const [interviewType, setInterviewType] = useState(
    candidature?.typeEntretien || ''
  )

  if (!candidature) {
    return null
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'postule':
        return '#ff9800'
      case 'accepte':
        return '#4caf50'
      case 'refuse':
        return '#f44336'
      case 'entretien':
        return '#2196f3'
      default:
        return '#666'
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'postule':
        return t('profile.applied')
      case 'accepte':
        return t('profile.accepted')
      case 'refuse':
        return t('profile.rejected')
      case 'entretien':
        return t('profile.interview')
      default:
        return status
    }
  }

  const getInterviewTypeLabel = (type) => {
    switch (type) {
      case 'phone':
        return t('profile.phoneInterview')
      case 'video':
        return t('profile.videoInterview')
      case 'inperson':
        return t('profile.inPersonInterview')
      case 'technical':
        return t('profile.technicalInterview')
      default:
        return type
    }
  }

  const handleNotesChange = (event) => {
    setCompanyNotes(event.target.value)
  }

  const handleFileChange = (event) => {
    setCvFile(event.target.files[0])
  }

  const handleInterviewDateChange = (event) => {
    setInterviewDate(event.target.value)
  }

  const handleInterviewTypeChange = (event) => {
    setInterviewType(event.target.value)
  }

  const handleStatusChange = (event) => {
    setEditedStatus(event.target.value)
    // If status is changed from entretien, clear interview fields
    /* if (event.target.value !== 'entretien') {
      setInterviewDate('')
      setInterviewType('')
    } */
  }

  const handleSaveChanges = async () => {
    setLoading(true)
    try {
      // Here you would make API calls to update the candidature
      let result = await Api.candidature.edit(candidature.id, {
        status: editedStatus,
        notesEntreprise: companyNotes,
        dateEntretien: interviewDate,
        typeEntretien: interviewType,
      })

      const handling = handlingErrors(result)
      if (handling.status === 200) {
        // For now, we'll just show a success message
        toast.success(t('profile.candidatureUpdated'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        })
        onSave()
      } else {
        toast.error(t('profile.updateError'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      scroll={'paper'}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : '16px',
          p: fullScreen ? 0 : '8px',
          m: fullScreen ? 0 : undefined,
          maxHeight: fullScreen ? '100%' : '90vh',
          ...(fullScreen && {
            display: 'flex',
            flexDirection: 'column',
          }),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          pb: 2,
          flexShrink: 0,
          borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
          backgroundColor: 'rgba(249, 250, 251, 0.5)',
        }}
      >
        <Typography
          variant={fullScreen ? 'subtitle1' : 'h6'}
          sx={{ fontWeight: 600, color: '#1f2937', pr: 1, lineHeight: 1.3 }}
        >
          {t('profile.applicationDetails')}
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{ color: '#6B7280', flexShrink: 0 }}
          aria-label={t('button.close', 'Fermer')}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 3 },
          pt: { xs: '16px !important', sm: '32px !important' },
          flex: fullScreen ? 1 : undefined,
          overflow: 'auto',
        }}
      >
        <Grid container>
          {/* Header Information */}
          <Grid item xs={12}>
            <HeaderSection>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 2, sm: 0 },
                  mb: { xs: 2, sm: 3 },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', color: '#1F2937' }}
                >
                  {t('profile.application')} #{candidature.id}
                </Typography>
                {role === ROLE_ENTREPRISE ? (
                  <FormControl
                    sx={{
                      minWidth: { sm: 140 },
                      width: { xs: '100%', sm: 'auto' },
                    }}
                    size="small"
                  >
                    <InputLabel>{t('profile.status')}</InputLabel>
                    <Select
                      value={editedStatus}
                      onChange={handleStatusChange}
                      label={t('profile.status')}
                    >
                      <MenuItem value="postule">
                        {t('profile.applied')}
                      </MenuItem>
                      <MenuItem value="entretien">
                        {t('profile.interview')}
                      </MenuItem>
                      <MenuItem value="accepte">
                        {t('profile.accepted')}
                      </MenuItem>
                      <MenuItem value="refuse">
                        {t('profile.rejected')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <StatusChip
                    label={getStatusLabel(candidature.status)}
                    status={candidature.status}
                  />
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 2 },
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCalendar}
                    fontSize={16}
                    color={lightTheme.palette.primary.main}
                    style={{ marginTop: 3, flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, wordBreak: 'break-word' }}
                  >
                    {t('profile.applicationDate')}:{' '}
                    {moment(candidature.date).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Box>

                {/* {candidature.besoinEntreprise?.entreprise && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <FontAwesomeIcon
                          icon={faBuilding}
                          fontSize={16}
                          color={lightTheme.palette.primary.main}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {t('profile.company')}:{' '}
                          {candidature.besoinEntreprise.entreprise.raisonSocial}
                        </Typography>
                      </Box>
                    )} */}
              </Box>
            </HeaderSection>
          </Grid>

          {/* Motivation Letter */}
          {candidature.motivation && (
            <Grid item xs={12}>
              <InfoSection>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {t('profile.motivationLetter')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}
                >
                  {candidature.motivation}
                </Typography>
              </InfoSection>
            </Grid>
          )}

          {/* CV Information */}
          {candidature.cv && (
            <Grid item xs={12}>
              <InfoSection>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {t('profile.cv')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faFile}
                    fontSize={16}
                    color={lightTheme.palette.primary.main}
                    style={{ marginTop: 3, flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    onClick={() => {
                      downloadFile(
                        `${FILE_URL}${candidature.cv.nom.split('/').pop()}`
                      )
                    }}
                    sx={{
                      color: '#1976D2',
                      cursor: 'pointer',
                      fontWeight: 500,
                      wordBreak: 'break-word',
                      '&:hover': {
                        color: '#1565C0',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {candidature.cv.nom}
                  </Typography>
                </Box>
              </InfoSection>
            </Grid>
          )}

          {/* Interview Information */}
          {(editedStatus === 'entretien' || candidature.dateEntretien) && (
            <Grid item xs={12}>
              <InterviewSection>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', mb: 3, color: '#1976D2' }}
                >
                  {t('profile.interviewInformation')}
                </Typography>
                {role === ROLE_ENTREPRISE && editedStatus === 'entretien' ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, mb: 1 }}
                      >
                        {t('profile.interviewDate')} *
                      </Typography>
                      <TextField
                        type="datetime-local"
                        value={interviewDate}
                        onChange={handleInterviewDateChange}
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          },
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, mb: 1 }}
                      >
                        {t('profile.interviewType')}
                      </Typography>
                      <FormControl
                        sx={{ minWidth: 200 }}
                        size="small"
                        fullWidth
                      >
                        <InputLabel>{t('profile.interviewType')}</InputLabel>
                        <Select
                          value={interviewType}
                          onChange={handleInterviewTypeChange}
                          label={t('profile.interviewType')}
                        >
                          <MenuItem value="phone">
                            {t('profile.phoneInterview')}
                          </MenuItem>
                          <MenuItem value="video">
                            {t('profile.videoInterview')}
                          </MenuItem>
                          <MenuItem value="inperson">
                            {t('profile.inPersonInterview')}
                          </MenuItem>
                          <MenuItem value="technical">
                            {t('profile.technicalInterview')}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Typography variant="body2">
                      <strong>{t('profile.interviewDate')}:</strong>{' '}
                      {candidature.dateEntretien
                        ? moment(candidature.dateEntretien).format(
                            'DD/MM/YYYY HH:mm'
                          )
                        : t('profile.noInterviewScheduled')}
                    </Typography>
                    {candidature.typeEntretien && (
                      <Typography variant="body2">
                        <strong>{t('profile.interviewType')}:</strong>{' '}
                        {getInterviewTypeLabel(candidature.typeEntretien)}
                      </Typography>
                    )}
                  </Box>
                )}
              </InterviewSection>
            </Grid>
          )}

          {/* Company Notes */}
          {role === ROLE_ENTREPRISE || candidature.notesEntreprise ? (
            <Grid item xs={12}>
              <NotesSection>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', mb: 3, color: '#2E7D32' }}
                >
                  {t('profile.companyNotes')}
                </Typography>
                {role === ROLE_ENTREPRISE ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={companyNotes}
                    onChange={handleNotesChange}
                    placeholder={t('profile.addCompanyNotes')}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      },
                    }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-line',
                      lineHeight: 1.6,
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      padding: 2,
                      borderRadius: 1,
                    }}
                  >
                    {candidature.notesEntreprise || t('profile.noNotes')}
                  </Typography>
                )}
              </NotesSection>
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      {role === ROLE_ENTREPRISE && (
        <DialogActions
          sx={{
            px: { xs: 2, sm: 4 },
            py: { xs: 2, sm: 3 },
            gap: 2,
            justifyContent: 'center',
            backgroundColor: 'rgba(249, 250, 251, 0.8)',
            borderTop: '1px solid rgba(229, 231, 235, 0.8)',
            flexDirection: { xs: 'column', sm: 'row' },
            flexShrink: 0,
            '& > *': {
              width: { xs: '100%', sm: 'auto' },
              m: 0,
            },
          }}
        >
          <OutlineButton onClick={onClose} fullWidth={fullScreen}>
            {t('button.cancel')}
          </OutlineButton>
          <GradientButton onClick={handleSaveChanges} fullWidth={fullScreen}>
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                {t('profile.saving')}
              </>
            ) : (
              t('button.save')
            )}
          </GradientButton>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default DetailCandidature
